import React, { useCallback, useMemo } from 'react'
import { DataTool } from '@ztechapps/webui.data-tools'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, NullComponent, ShowIf } from '@ztechapps/webui.util'
import { DEFAULT_TASK_FILTER_STATE, taskFieldsConfig, TaskFilterPresetEnum } from '@ztechapps/task.common'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import isEmpty from 'lodash/isEmpty'
import type { UseListResult } from '@ztechapps/common.hook'
import type { TaskFilterState } from '@ztechapps/task.common'
import { useTaskFilterBarContext } from './Context'
import { TaskFilterChips } from './TaskFilterChips'
import { TaskFilterBarAddButton } from './TaskFilterBarAddButton'
import styles from './TaskFilterBar.module.scss'

const noop = () => Promise.resolve()
const ALL_TASK_STATUSES = Object.values(TaskStatusEnum)

type PresetConfig = {
  key: TaskFilterPresetEnum
  label: string
  icon: string
}

const PRESETS: PresetConfig[] = [
  { key: TaskFilterPresetEnum.MyTasks, label: 'My Tasks', icon: 'user' },
  { key: TaskFilterPresetEnum.DueThisWeek, label: 'My Due This Week', icon: 'calendar-week' },
  { key: TaskFilterPresetEnum.Overdue, label: 'My Overdue', icon: 'clock' },
]

const hasNonDefaultFilter = (filterState: TaskFilterState): boolean => {
  if (filterState.statuses.length < ALL_TASK_STATUSES.length) return true
  if (!isEmpty(filterState.projectIds)) return true
  if (!isEmpty(filterState.workOrderIds)) return true
  if (filterState.assigneeIds.length > DEFAULT_TASK_FILTER_STATE.assigneeIds.length) return true
  if (filterState.dateRange.presetName ?? filterState.dateRange.customStart ?? filterState.dateRange.customEnd)
    return true
  return false
}

type TaskFilterBarInlineProps = {
  listAction?: UseListResult<any>
  leadingActions?: React.ReactNode
}

/**
 * Shared task filter bar with presets, filter chips, and DataTool search.
 * Renders identically across List, Board, and Calendar views.
 *
 * @param listAction - the useList result providing onSearch/onFilter for the DataTool search
 */
export const TaskFilterBarInline = ({ listAction, leadingActions }: TaskFilterBarInlineProps) => {
  const { filterState, clearAll, applyPreset, activePreset } = useTaskFilterBarContext()

  const handlePresetClick = useCallback(
    (preset: TaskFilterPresetEnum) => {
      if (activePreset === preset) {
        clearAll()
      } else {
        applyPreset(preset)
      }
    },
    [activePreset, clearAll, applyPreset]
  )

  const showReset = useMemo(() => hasNonDefaultFilter(filterState), [filterState])

  return (
    <HorizontalStack gap={4} className="flex-wrap align-items-center">
      <ShowIf condition={!!leadingActions}>
        {leadingActions}
        <div className={`${styles.chipsDivider} flex-shrink-0`} />
      </ShowIf>

      <HorizontalStack gap={1}>
        {PRESETS.map((preset) => (
          <BusyButton
            key={preset.key}
            bsSize="sm"
            variant={activePreset === preset.key ? 'primary' : 'outline-primary'}
            icon={preset.icon}
            text={preset.label}
            onClick={() => handlePresetClick(preset.key)}
            active={activePreset === preset.key}
          />
        ))}
      </HorizontalStack>

      <div>
        <TaskFilterChips />
      </div>

      <ShowIf condition={showReset}>
        <span role="button" className="text-primary small" onClick={() => clearAll()}>
          Reset
        </span>
      </ShowIf>

      <div className={`${styles.chipsDivider} flex-shrink-0`} />

      {/* DataTool search — connected to server-side useList result */}
      <ShowIf condition={!!listAction}>
        <div className="flex-fill">
          <DataTool
            {...listAction}
            fieldsConfig={taskFieldsConfig}
            filterCategory="filter_task_shared"
            onAdd={noop}
            components={{ sort: NullComponent, filter: NullComponent, add: TaskFilterBarAddButton }}
            searchPlaceHolder="Search tasks..."
          />
        </div>
      </ShowIf>
    </HorizontalStack>
  )
}
